import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Store, Camera, Loader2, ArrowLeft, Eye, EyeOff, CheckCircle2, MailCheck } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export default function AuthPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, activate, user } = useAuth();

  const pathMode = location.pathname === "/reset-password" ? "reset"
    : location.pathname === "/forgot-password" ? "forgot"
    : location.pathname === "/activate" ? "activate"
    : (params.get("mode") === "register" ? "register" : "login");
  const [mode, setMode] = useState(pathMode);
  const [role, setRole] = useState(params.get("role") || "creator");
  const rawRedirect = params.get("redirect") || "";
  // Only allow internal same-app paths — blocks open-redirect via ?redirect=//evil.com or https://evil.com
  const redirectTo = /^\/(?!\/)/.test(rawRedirect) ? rawRedirect : "";
  const srcParam = params.get("src") || "";
  const refId = params.get("ref_id") || "";
  const resetToken = params.get("token") || "";
  const activationToken = mode === "activate" ? (params.get("token") || "") : "";

  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [agree, setAgree] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [activation, setActivation] = useState({ loading: mode === "activate", valid: false, business_name: "", email: "", contact_name: "" });
  const handledRef = useRef(false);

  useEffect(() => {
    if (mode !== "activate") return;
    if (!activationToken) { setActivation((a) => ({ ...a, loading: false, valid: false })); return; }
    api.get(`/auth/activation/${activationToken}`)
      .then((r) => setActivation({ loading: false, ...r.data }))
      .catch(() => setActivation({ loading: false, valid: false }));
  }, [mode, activationToken]);

  useEffect(() => {
    if (handledRef.current) return;
    // Do not bounce logged-in users away from the reset/forgot/activate flows (they may
    // have opened a link while signed in).
    if (user && mode !== "reset" && mode !== "forgot" && mode !== "activate") navigate(redirectTo || "/dashboard", { replace: true });
  }, [user, navigate, redirectTo, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    handledRef.current = true;
    try {
      if (mode === "register") {
        const inviteCode = (() => { try { return localStorage.getItem("nf_invite") || null; } catch { return null; } })();
        const storedSrc = (() => { try { return localStorage.getItem("nf_src") || null; } catch { return null; } })();
        const acquisition_source = srcParam || storedSrc || (inviteCode ? "creator_invite" : "direct");
        await register({ ...form, role, accept_terms: agree, marketing_opt_in: marketing,
          invite_code: inviteCode, acquisition_source, ref_id: refId || null });
        try { localStorage.removeItem("nf_invite"); localStorage.removeItem("nf_src"); } catch {}
        toast.success("Welcome to Nearfluence!");
        if (redirectTo) navigate(redirectTo);
        else navigate(role === "business" ? "/business/profile" : "/creator/profile");
      } else if (mode === "activate") {
        if (form.password.length < 6) { setError("Password must be at least 6 characters."); setBusy(false); return; }
        if (form.password !== confirmPw) { setError("Passwords do not match."); setBusy(false); return; }
        await activate(activationToken, form.password);
        toast.success("Account activated — welcome to Nearfluence!");
        navigate("/dashboard");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        setForgotSent(true);
      } else if (mode === "reset") {
        if (form.password.length < 6) { setError("Password must be at least 6 characters."); setBusy(false); return; }
        if (form.password !== confirmPw) { setError("Passwords do not match."); setBusy(false); return; }
        await api.post("/auth/reset-password", { token: resetToken, password: form.password });
        setResetDone(true);
        toast.success("Password successfully changed");
      } else {
        const u = await login(form.email, form.password);
        toast.success("Signed in");
        if (redirectTo) navigate(redirectTo);
        else navigate(u.role === "admin" ? "/admin" : "/dashboard");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const headings = {
    login: ["Welcome back", "Sign in to your Nearfluence account."],
    register: ["Create your account", "Join free during our launch."],
    forgot: ["Forgot your password?", "Enter your email and we’ll send you a reset link."],
    reset: ["Set a new password", "Choose a new password for your Nearfluence account."],
    activate: ["Activate your account", "Set a password to activate your Nearfluence business account."],
  };
  const [title, subtitle] = headings[mode] || headings.login;

  return (
    <div className="flex min-h-screen bg-[#FAF9F6]">
      <div className="hidden w-1/2 flex-col justify-between bg-zinc-900 p-12 lg:flex">
        <Logo onClick={() => navigate("/")} light />
        <div>
          <h2 className="font-display text-4xl font-extrabold leading-tight text-white">
            Find influence <span className="text-emerald-400">closer to home.</span>
          </h2>
          <p className="mt-4 max-w-md text-zinc-300">
            Nearfluence connects local businesses with nearby creators right across the UK.
          </p>
        </div>
        <p className="text-sm text-zinc-500">Local creators. Local businesses. Real influence.</p>
      </div>

      <div className="flex w-full items-center justify-center px-4 py-10 lg:w-1/2">
        <div className="w-full max-w-md">
          <button onClick={() => navigate("/")} className="mb-6 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 lg:hidden">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="lg:hidden"><Logo onClick={() => navigate("/")} /></div>

          {/* -------- Forgot password: confirmation state -------- */}
          {mode === "forgot" && forgotSent ? (
            <div className="mt-8" data-testid="forgot-confirmation">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                <MailCheck className="h-6 w-6 text-emerald-600" />
              </div>
              <h1 className="mt-5 font-display text-3xl font-bold tracking-tight text-zinc-900">Check your inbox</h1>
              <p className="mt-2 text-zinc-600">
                If an account exists for that email, we’ve sent a password reset link. It expires in 30 minutes.
              </p>
              <p className="mt-2 text-sm text-zinc-400">Didn’t get it? Check your spam folder or try again in a few minutes.</p>
              <Button data-testid="forgot-back-to-login" onClick={() => { setMode("login"); setForgotSent(false); navigate("/auth?mode=login"); }}
                className="mt-6 h-11 w-full rounded-full bg-emerald-600 text-base hover:bg-emerald-700">
                Back to sign in
              </Button>
            </div>
          ) : mode === "reset" && resetDone ? (
            /* -------- Reset password: success state -------- */
            <div className="mt-8" data-testid="reset-success">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <h1 className="mt-5 font-display text-3xl font-bold tracking-tight text-zinc-900">Password changed</h1>
              <p className="mt-2 text-zinc-600">Your password has been successfully changed. You can now sign in with your new password.</p>
              <Button data-testid="reset-go-login" onClick={() => navigate("/auth?mode=login")}
                className="mt-6 h-11 w-full rounded-full bg-emerald-600 text-base hover:bg-emerald-700">
                Go to sign in
              </Button>
            </div>
          ) : mode === "reset" && !resetToken ? (
            /* -------- Reset password: missing/invalid link -------- */
            <div className="mt-8" data-testid="reset-invalid-link">
              <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-900">Invalid reset link</h1>
              <p className="mt-2 text-zinc-600">This password reset link is missing or invalid. Please request a new one.</p>
              <Button data-testid="reset-request-again" onClick={() => navigate("/forgot-password")}
                className="mt-6 h-11 w-full rounded-full bg-emerald-600 text-base hover:bg-emerald-700">
                Request a new link
              </Button>
            </div>
          ) : mode === "activate" && activation.loading ? (
            <div className="mt-16 flex justify-center" data-testid="activate-loading"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
          ) : mode === "activate" && !activation.valid ? (
            <div className="mt-8" data-testid="activate-invalid-link">
              <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-900">Invalid activation link</h1>
              <p className="mt-2 text-zinc-600">This activation link is invalid, has expired, or has already been used. Please ask the Nearfluence team to resend your invite.</p>
              <Button data-testid="activate-go-login" onClick={() => navigate("/auth?mode=login")}
                className="mt-6 h-11 w-full rounded-full bg-emerald-600 text-base hover:bg-emerald-700">
                Go to sign in
              </Button>
            </div>
          ) : (
            <>
              <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-zinc-900">{title}</h1>
              <p className="mt-1.5 text-zinc-500">{subtitle}</p>

              {mode === "activate" && activation.valid && (
                <div data-testid="activate-business-info" className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-sm font-semibold text-emerald-800">{activation.business_name}</p>
                  <p className="text-xs text-emerald-700">{activation.email}{activation.contact_name ? ` · ${activation.contact_name}` : ""}</p>
                </div>
              )}

              {mode === "register" && (
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {[
                    { key: "creator", label: "Creator", Icon: Camera },
                    { key: "business", label: "Business", Icon: Store },
                  ].map((r) => (
                    <button key={r.key} type="button" data-testid={`role-${r.key}`} onClick={() => setRole(r.key)}
                      className={`flex flex-col items-center gap-2 rounded-2xl border-2 p-4 transition-all ${
                        role === r.key ? "border-emerald-600 bg-emerald-50" : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
                      <r.Icon className={`h-6 w-6 ${role === r.key ? "text-emerald-600" : "text-zinc-400"}`} />
                      <span className={`text-sm font-semibold ${role === r.key ? "text-emerald-700" : "text-zinc-600"}`}>{r.label}</span>
                    </button>
                  ))}
                </div>
              )}

              <form onSubmit={submit} className="mt-6 space-y-4">
                {mode === "register" && (
                  <div>
                    <Label htmlFor="name">{role === "business" ? "Business name" : "Full name"}</Label>
                    <Input id="name" data-testid="auth-name" required value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11" placeholder={role === "business" ? "The Corner Café" : "Sarah Whitfield"} />
                  </div>
                )}

                {(mode === "login" || mode === "register" || mode === "forgot") && (
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" data-testid={mode === "forgot" ? "forgot-email" : "auth-email"} required value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11" placeholder="you@example.com" />
                  </div>
                )}

                {(mode === "login" || mode === "register") && (
                  <div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      {mode === "login" && (
                        <button type="button" data-testid="auth-forgot-link" onClick={() => { setMode("forgot"); setError(""); navigate("/forgot-password"); }}
                          className="text-sm font-medium text-emerald-600 hover:underline">Forgot password?</button>
                      )}
                    </div>
                    <Input id="password" type="password" data-testid="auth-password" required value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 h-11" placeholder="••••••••" />
                  </div>
                )}

                {mode === "reset" && (
                  <>
                    <div>
                      <Label htmlFor="new-password">New password</Label>
                      <div className="relative mt-1.5">
                        <Input id="new-password" type={showPw ? "text" : "password"} data-testid="reset-password" required value={form.password}
                          onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 pr-11" placeholder="••••••••" />
                        <button type="button" data-testid="reset-toggle-visibility" onClick={() => setShowPw((s) => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
                          {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="confirm-password">Confirm new password</Label>
                      <Input id="confirm-password" type={showPw ? "text" : "password"} data-testid="reset-confirm-password" required value={confirmPw}
                        onChange={(e) => setConfirmPw(e.target.value)} className="mt-1.5 h-11" placeholder="••••••••" />
                    </div>
                    <p className="text-xs text-zinc-500" data-testid="password-requirements">Use at least 6 characters.</p>
                  </>
                )}

                {mode === "activate" && activation.valid && (
                  <>
                    <div>
                      <Label htmlFor="activate-password">Create a password</Label>
                      <div className="relative mt-1.5">
                        <Input id="activate-password" type={showPw ? "text" : "password"} data-testid="activate-password" required value={form.password}
                          onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 pr-11" placeholder="••••••••" />
                        <button type="button" data-testid="activate-toggle-visibility" onClick={() => setShowPw((s) => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
                          {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="activate-confirm">Confirm password</Label>
                      <Input id="activate-confirm" type={showPw ? "text" : "password"} data-testid="activate-confirm-password" required value={confirmPw}
                        onChange={(e) => setConfirmPw(e.target.value)} className="mt-1.5 h-11" placeholder="••••••••" />
                    </div>
                    <p className="text-xs text-zinc-500">Use at least 6 characters. You'll review and accept our Terms of Service next.</p>
                  </>
                )}

                {error && <p data-testid="auth-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600">{error}</p>}

                {mode === "register" && (
                  <div className="space-y-3">
                    <label className="flex cursor-pointer items-start gap-2.5">
                      <Checkbox data-testid="auth-accept-terms" checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" />
                      <span className="text-sm text-zinc-600">I agree to the Nearfluence <Link to="/terms" target="_blank" className="font-medium text-emerald-600 hover:underline">Terms of Use</Link> and acknowledge the <Link to="/privacy" target="_blank" className="font-medium text-emerald-600 hover:underline">Privacy Policy</Link>.</span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-2.5">
                      <Checkbox data-testid="auth-marketing" checked={marketing} onCheckedChange={(v) => setMarketing(!!v)} className="mt-0.5" />
                      <span className="text-sm text-zinc-500">Send me occasional Nearfluence tips and updates by email (optional).</span>
                    </label>
                  </div>
                )}

                <Button type="submit" data-testid={mode === "forgot" ? "forgot-submit" : mode === "reset" ? "reset-submit" : mode === "activate" ? "activate-submit" : "auth-submit"}
                  disabled={busy || (mode === "register" && !agree)} className="h-11 w-full rounded-full bg-emerald-600 text-base hover:bg-emerald-700">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" />
                    : mode === "register" ? "Create account"
                    : mode === "forgot" ? "Send reset link"
                    : mode === "reset" ? "Change password"
                    : mode === "activate" ? "Activate account"
                    : "Sign in"}
                </Button>
              </form>

              {mode === "forgot" ? (
                <p className="mt-6 text-center text-sm text-zinc-500">
                  Remembered it?{" "}
                  <button data-testid="auth-toggle" onClick={() => { setMode("login"); setError(""); navigate("/auth?mode=login"); }}
                    className="font-semibold text-emerald-600 hover:underline">Back to sign in</button>
                </p>
              ) : (mode === "login" || mode === "register") ? (
                <p className="mt-6 text-center text-sm text-zinc-500">
                  {mode === "register" ? "Already have an account?" : "New to Nearfluence?"}{" "}
                  <button data-testid="auth-toggle" onClick={() => { setMode(mode === "register" ? "login" : "register"); setError(""); }}
                    className="font-semibold text-emerald-600 hover:underline">
                    {mode === "register" ? "Sign in" : "Create an account"}
                  </button>
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
